import { useContext } from 'react'

// Third party
import { AddCircleOutline, RemoveCircleOutline } from '@mui/icons-material'
import clsx from 'clsx'
import { Link } from 'react-router-dom'
import { components } from 'react-select'
import { useParams } from 'react-router'

// Component imports
import Accordion from '../../../../components/accordion/common/Accordion'
import DataNotAvailable from '../../../../components/common/alerts/data-not-available/DataNotAvailable'
import CustomReactSelect from '../../../../components/common/select/react-select/CustomReactSelect'
import CompetitorEventRegisterWrapper from '../../CompetitorEventRegisterWrapper'
import { GuardianBadge } from '../../event-registration-tabs/components/GuardianBadge'

// Styles
import { CompetitorReactSelectCustomStyle } from '../../../../customStyles/CompetitorReactSelectCustomStyle'
import './competitorEventRegisterClassesTab.css'

// Redux
// import { selectedEvent } from '../../../../store/events/eventsSlice'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import {
  selectPaperworkTabData,
  selectRegistration,
  selectTeamTabData,
  setRegistrationAc,
} from '../../../../store/registration/registrationSlice'

// Services
import FirestoreService from '../../../../services/firestoreService'
import helpers from '../../../../commonHelpers/helpers'
import useToasterHelper from '../../../../helpers/ToasterHelper'
import { calculateAge } from '../../../../models/interface.helper'
import { HandleModalContext } from '../../../../layout/mainlayout/MainLayout'
import { getRegisterTeamData } from '../../../../helpers/teamMembers'

import useTeamMembers from '../../../../hooks/useTeamMembers'

import { ITeamTab } from '../../../../models/event-registered-users/event-registered-users.interface'
import ITypes from '../../event-registration-tabs/hooks/useEventRegistrationTabs.types'
import { IOption } from '../../../../components/inputs/InputNewMemberRow'

// Constants
import { CONST } from '../../../../const/const'
// import { MESSAGES_CONST } from '../../../../const/messages-const'
import { MODAL_CONSTS } from '../../../../const/modal-const'
import { MEMBER_ROLES } from '../../../../const/ui/ui-register.const'
import { HORSE_NAME_DEFAULT } from '../../../../models/registrations-by-day/registrationByDay.interface'

const COLLECTIONS = CONST.DATA.FIRESTORE.LATEST.COLLECTIONS

// Types
type Props = {
  title?: string
  description?: string
}

// Constants
const TO_REPLACE = '[NAME]'

const GENERATE_NAME_PIC_URL =
  'https://ui-avatars.com/api/?name=[NAME]&background=FFFFFF&format=svg&bold=true&color=BCC6D5&rounded=true'

const IconCard = (props: {
  icon: string
  riderName: string | null
  horseName: string | null
  teamMateId?: string | null
}) => {
  const { icon, riderName, horseName, teamMateId } = props

  return (
    <div className="flex items-center font-normal">
      <div className="mr-2 rounded-full">
        <Link to={`${CONST.ROUTES.USER_PROFILE.URL}/${teamMateId}`} className="cursor-pointer">
          <div className="w-[48px] h-[48px]">
            <img
              src={icon}
              alt="horseIcon"
              className="w-full h-full object-cover rounded-lg shadow-sm"
              onError={(e) =>
                ((e.target as any).src = GENERATE_NAME_PIC_URL.replace(
                  TO_REPLACE,
                  helpers.generateName(riderName ?? '')
                ))
              }
            />
          </div>
        </Link>
      </div>
      <div className="flex flex-col">
        <div className="text-SeabiscuitDark200ThemeColor capitalize text-base">
          {riderName ?? 'Unknown'}
        </div>
        <div className="text-SeabiscuitLightParagraphTextColor text-sm">
          <div className="flex gap-2 items-center ">
            <img src={'assets/og_icons/YearofHorse.svg'} alt="horseIcon" className="w-4" />
            {horseName ?? HORSE_NAME_DEFAULT}
          </div>
        </div>
      </div>
    </div>
  )
}

const CompetitorEventsRegisterPaperworkTab = ({ title, description }: Props) => {
  const { userId } = useParams<ITypes['IParams']>()

  // Hooks and vars
  let isMature = false

  const handleModal = useContext(HandleModalContext)?.handleModal
  const toastFunction = useToasterHelper()
  const dispatch = useAppDispatch()

  const {
    onAddRiderButtonClick,
    onRemoveRiderTeamMemberClick,
    onRiderTeamMemberRoleChange,
    onRiderTeamMemberChange,
  } = useTeamMembers()

  // const selectedEvent_ = useAppSelector(selectedEvent)
  const paperworkTabData = useAppSelector(selectPaperworkTabData)
  const teamTabData = useAppSelector(selectTeamTabData)
  const registration = useAppSelector(selectRegistration)

  // Functions
  const checkIsMinorFn = (dob?: Date | string | null): boolean => {
    const MINORITY_AGE = 18
    let valueToReturn = false

    if (!dob || dob === '') {
      return valueToReturn
    } else {
      return calculateAge(dob) < MINORITY_AGE
    }
  }

  return (
    <CompetitorEventRegisterWrapper title={title} description={description}>
      <div id="rtc">
        {!paperworkTabData?.[0] ? (
          <DataNotAvailable
            mode="text"
            containerClassName="text-SeabiscuitDark200ThemeColor text-nr 2xl:text-base"
            text=" Please register a user in this event"
          />
        ) : null}
        {paperworkTabData?.[0]
          ? paperworkTabData.map((currentPaperworkRow, index) => {
              isMature = !checkIsMinorFn(currentPaperworkRow.riderDob)

              const guardian = currentPaperworkRow.ridersTeamMembers.find(
                (member) => member.teamMemberRole === MEMBER_ROLES.GUARDIAN
              )
              const isGuardianIsMature = guardian ? checkIsMinorFn(guardian.teamMemberDob) : false

              return (
                <Accordion
                  key={`${JSON.stringify(currentPaperworkRow)}${index}`}
                  icon={<AddCircleOutline className="add_icon text-[red]" />}
                  className="mb-2 !rounded-lg"
                  childrenContainerClassName="!pt-0"
                  header={
                    <div
                      className={`text-SeabiscuitDark200ThemeColor flex flex-col md:flex-row justify-start md:justify-between md:items-center`}
                    >
                      <div
                        className={'flex flex-wrap items-start flex-col-reverse md:flex-row gap-3'}
                      >
                        <IconCard
                          horseName={currentPaperworkRow?.horseName ?? null}
                          icon={currentPaperworkRow.riderProfilePicture ?? ''}
                          riderName={currentPaperworkRow?.riderName ?? null}
                          teamMateId={currentPaperworkRow.riderId ?? null}
                        />
                        {!isMature && (
                          <>
                            <GuardianBadge
                              hasGuardian={!!guardian}
                              isLabel
                              classes={'mt-1'}
                              isGuardianIsMinor={isGuardianIsMature}
                            />
                          </>
                        )}
                      </div>

                      <div className="flex items-center details mt-2 md:mt-0">
                        <div className="font-normal text-[14px] text-SeabiscuitDark200ThemeColor whitespace-nowrap lowercase">
                          {currentPaperworkRow.ridersTeamMembers.length ?? 0} team member
                          {currentPaperworkRow.ridersTeamMembers.length > 1 ? 's' : ''}
                        </div>
                      </div>
                    </div>
                  }
                  headerTextClassName="text-SeabiscuitDark200ThemeColor capitalize w-full pr-2"
                  activeIcon={<RemoveCircleOutline className="add_icon text-[red]" />}
                >
                  <div id={`container${index}`}>
                    {currentPaperworkRow.ridersTeamMembers.map(
                      (currRidersTeamMember, ridersTeamMemberIndex) => {
                        isMature = !checkIsMinorFn(currRidersTeamMember.teamMemberDob)

                        return (
                          <div
                            key={`container${ridersTeamMemberIndex}${index}`}
                            className={clsx(
                              'flex flex-col lg:flex-row transition-all items-center relative pr-8',
                              ridersTeamMemberIndex <
                                currentPaperworkRow.ridersTeamMembers.length - 1 && 'mb-2'
                            )}
                            title={
                              !isMature
                                ? "The team member is either minor or he/she haven't set his/her dob"
                                : ''
                            }
                          >
                            <CustomReactSelect
                              targetId={`container${index}`}
                              isMulti={false}
                              options={[
                                ...teamTabData?.map((teamMember) => ({
                                  label: teamMember.memberName ?? '',
                                  value: teamMember.memberId ?? '',
                                })),
                                { label: 'Add Team Member', value: 'add-team-member' },
                              ]}
                              menuShouldScrollIntoView={true}
                              styles={CompetitorReactSelectCustomStyle}
                              className={clsx(
                                'w-full rounded-xl p-1 border focus:ring-0 focus:outline-0 flex-grow lg:mr-2',
                                currRidersTeamMember.teamMemberName
                                  ? 'bg-[#F6F7FB] border-transparent'
                                  : 'border-SeabiscuitLightThemeColorD3'
                              )}
                              value={{
                                label: currRidersTeamMember.teamMemberName ?? '',
                                value: currRidersTeamMember.teamMemberId ?? '',
                              }}
                              isDisabled={
                                currRidersTeamMember.teamMemberRole ===
                                CONST.UI.TEAM_MEMBERS_ROLES.RIDER
                              }
                              placeholder={
                                <div className="flex text-SeabiscuitDark200ThemeColor/50">
                                  Select...
                                </div>
                              }
                              onChange={async (option) => {
                                const value = (option as IOption)?.value

                                if (value === 'add-team-member') {
                                  const { userTeamMembers, competitor } = await getRegisterTeamData(
                                    {
                                      teamTabData,
                                      userId,
                                    }
                                  )
                                  handleModal?.(true, MODAL_CONSTS.REGISTER_TEAM, {
                                    userTeamMembers: userTeamMembers,
                                    competitor: competitor,
                                    onSave: async (teamTabData: ITeamTab) => {
                                      const membersIds = teamTabData.map(
                                        (member) => `${member.memberId}`
                                      )
                                      await FirestoreService.updateItem(
                                        COLLECTIONS.EVENT_REGISTERED_USERS.NAME,
                                        registration?.id,
                                        {
                                          membersIds: membersIds,
                                          modified: new Date().toString(),
                                        }
                                      )

                                      if (registration) {
                                        dispatch(
                                          setRegistrationAc({
                                            ...registration,
                                            membersIds: membersIds,
                                          })
                                        )
                                      }

                                      toastFunction.success({
                                        message:
                                          'Team member added successfully. Please select from the list.',
                                      })
                                    },
                                  })
                                } else {
                                  const filtered = teamTabData?.filter(
                                    (member) => member.memberId === value
                                  )
                                  if (filtered.length > 0)
                                    await onRiderTeamMemberChange({
                                      paperworkIndex: index,
                                      currentTeamMember: filtered[0],
                                      ridersTeamMemberIndex: ridersTeamMemberIndex,
                                    })
                                }
                              }}
                              isClearable={false}
                              isSearchable={true}
                            />

                            <CustomReactSelect
                              targetId={`container${index}`}
                              isMulti={false}
                              options={CONST.UI.ROLES.filter(
                                (role) => role.value !== CONST.UI.TEAM_MEMBERS_ROLES.RIDER
                              )}
                              menuShouldScrollIntoView={true}
                              styles={CompetitorReactSelectCustomStyle}
                              value={
                                CONST.UI.ROLES.filter((current) => {
                                  return current.label === currRidersTeamMember.teamMemberRole
                                })[0] ?? ''
                              }
                              isDisabled={
                                currRidersTeamMember.teamMemberRole ===
                                CONST.UI.TEAM_MEMBERS_ROLES.RIDER
                              }
                              components={{
                                DropdownIndicator: (props) => {
                                  if (
                                    currRidersTeamMember.teamMemberRole ===
                                    CONST.UI.TEAM_MEMBERS_ROLES.RIDER
                                  )
                                    return null
                                  return <components.DropdownIndicator {...props} />
                                },
                              }}
                              placeholder={
                                <div className="flex text-SeabiscuitDark200ThemeColor/50">
                                  Select role...
                                </div>
                              }
                              onChange={async (e: any) => {
                                await onRiderTeamMemberRoleChange({
                                  paperworkIndex: index,
                                  teamMemberId: currRidersTeamMember.teamMemberId,
                                  teamMemberRole: e.value,
                                  ridersTeamMemberIndex: ridersTeamMemberIndex,
                                })
                              }}
                              className={clsx(
                                'rounded-xl p-1 focus:ring-0 focus:outline-0 border w-full lg:w-[250px] mt-2 lg:mt-0',
                                currRidersTeamMember.teamMemberRole
                                  ? 'bg-SeabiscuitGrayThemeColor border-transparent'
                                  : 'border-SeabiscuitLightThemeColorD3'
                              )}
                              isClearable={false}
                              isSearchable={true}
                            />

                            {currRidersTeamMember.teamMemberRole !==
                              CONST.UI.TEAM_MEMBERS_ROLES.RIDER && (
                              <span
                                className="flex items-center justify-center h-full text-SeabiscuitMainThemeColor cursor-pointer absolute right-0"
                                role="button"
                                onClick={() => {
                                  handleModal?.(true, MODAL_CONSTS.REMOVE_CONFIRMATION, {
                                    removeItem: async () =>
                                      await onRemoveRiderTeamMemberClick({
                                        paperworkIndex: index,
                                        teamMemberIndex: ridersTeamMemberIndex,
                                      }),
                                    alertText: (
                                      <div>
                                        <h5 className="font-normal text-xl text-SeabiscuitDark200ThemeColor">
                                          Are you sure you want to remove this team member?
                                        </h5>
                                      </div>
                                    ),
                                  })
                                }}
                              >
                                <img src={'assets/og_icons/Cancel.svg'} alt="delete" />
                              </span>
                            )}
                          </div>
                        )
                      }
                    )}
                  </div>

                  <div className="w-full flex lg:justify-end mt-4 pr-8">
                    <button
                      className="p-4 rounded-xl w-full text-sm text-SeabiscuitBlackThemeColor/50 underline transition-all bg-black/[4%] hover:no-underline"
                      onClick={async () => {
                        await onAddRiderButtonClick({
                          paperworkIndex: index,
                          row: currentPaperworkRow,
                        })
                      }}
                    >
                      Add Person
                    </button>
                  </div>
                </Accordion>
              )
            })
          : null}
      </div>
    </CompetitorEventRegisterWrapper>
  )
}

export default CompetitorEventsRegisterPaperworkTab
